import React from 'react';
import style from './MiniOrdersTable.module.css';
import { useWebsocketStore } from '../../../../../../../../stores/websocketStore/websocketProvider';
import { Body3, Body4 } from '../../../../../../../atomic/text/Text';
import { colors } from '../../../../../../../../colorVars';
import { CancelOrderButton } from '../../../../../../../atomic/buttons/cancelOrderButton/CancelOrderButton';
import { observer } from 'mobx-react-lite';
import { isOrderOpen } from '../../../../../../../../stores/websocketStore/utils/mungingUtils/orderStatusFilter';
import { MiniOrderTableRow } from './MiniOrderTableRow';
import { PendingTargetTableRow } from './PendingTargetTableRow';

// pulls every currently-watched-but-not-yet-placed target level across this trade plan's soft
// orders into a flat, renderable list. Only tactics that emit a `pendingTargets` array (RatchetBracket,
// GrabCents, UnderlyingPriceExit's stock branch) contribute rows here - LossLimit has no comparable
// concept of a cancelable order (it never rests one on any broker; it only market-flattens once
// breached), so it's deliberately not surfaced here.
const getPendingTargetRows = (tradePlanStore, tradePlanId, direction) => {
  const softOrderIds = tradePlanStore.softOrderIdsByTradePlan[tradePlanId] ?? [];
  const sideLabel = direction === 'long' ? 'STC' : 'BTC';
  return softOrderIds.flatMap((softOrderId) => {
    const softOrder = tradePlanStore.softOrdersById[softOrderId];
    const pendingTargets = softOrder?.value?.tacticValue?.pendingTargets ?? [];
    return pendingTargets.map((target) => {
      const cancelKeyType = target.level !== undefined ? 'level' : 'price';
      return {
        key: `${softOrderId}-${cancelKeyType}-${target[cancelKeyType]}`,
        price: Number(target.targetPrice ?? target.price),
        row: (
          <PendingTargetTableRow
            key={`${softOrderId}-${cancelKeyType}-${target[cancelKeyType]}`}
            completeSoftOrder={softOrder}
            cancelKey={target[cancelKeyType]}
            cancelKeyType={cancelKeyType}
            qty={target.qty}
            price={Number(target.targetPrice ?? target.price)}
            sideLabel={sideLabel}
          />
        ),
      };
    });
  });
};

export const MiniOrdersTable = observer(({ tradePlanId }) => {
  const { tradePlanStore } = useWebsocketStore();
  const tradePlan = tradePlanStore.tradePlans[tradePlanId];
  const direction = tradePlan.goLong ? 'long' : 'short';
  const orders = tradePlanStore.ordersByTradePlanId[tradePlanId] ?? null;

  const openOrderArray = orders
    ? Object.keys(orders).filter((orderId) => isOrderOpen(orders[orderId].status, 'TRADIER'))
    : [];
  const pendingRows = getPendingTargetRows(tradePlanStore, tradePlanId, direction);

  if (openOrderArray.length < 1 && pendingRows.length < 1)
    return (
      <div className={style.containerNoOrders}>
        <Body3 fontWeight={700} color={colors.lightGrey}>
          No orders to display.
        </Body3>
      </div>
    );

  const combinedRows = [
    ...openOrderArray.map((orderId) => ({
      price: Number(orders[orderId].price),
      row: <MiniOrderTableRow orderId={orderId} key={orderId} />,
    })),
    ...pendingRows,
  ].sort((a, b) => (direction === 'long' ? a.price - b.price : b.price - a.price));

  return (
    <div className={style.container}>
      <table className={style.table}>
        <thead>
          <tr>
            <th>
              <div className={style.cancelButtonWrapper}>
                <CancelOrderButton  width={15} />
              </div>
            </th>
            <th>
              <Body4 fontWeight={700} color={colors.lightGrey}>
                Side
              </Body4>
            </th>
            <th>
              <Body4 fontWeight={700} color={colors.lightGrey}>
                Qty.
              </Body4>
            </th>
            <th>
              <Body4 fontWeight={700} color={colors.lightGrey}>
                Price
              </Body4>
            </th>
          </tr>
        </thead>
        <tbody className={style.tbody}>
          {combinedRows.map(({ row }) => row)}
        </tbody>
      </table>
    </div>
  );
});
